import { createApi } from '@reduxjs/toolkit/query/react';
import { urls } from '../../utils/helpers/ApiClient';
import axiosFetchBase from '../axiosFetchBase';

// CMS reads document-bearing records through this adapter.  Once frozen, only
// `presentation` may replace display values; editable ids/workflow fields stay
// on the record for routing and forms.
const presentPhdRecord = (record) =>
  record?.presentation ? { ...record, ...record.presentation } : record;

const presentPhdResult = (result) => {
  if (Array.isArray(result)) return result.map(presentPhdRecord);
  if (Array.isArray(result?.data))
    return { ...result, data: result.data.map(presentPhdRecord) };
  return presentPhdRecord(result);
};

const withoutClientSchoolYear = (body) => {
  if (!body || typeof body !== 'object') {
    return body;
  }

  const serverOwnedBody = { ...body };
  delete serverOwnedBody.phd_school_year_id;

  return serverOwnedBody;
};

export const phdApi = createApi({
  reducerPath: 'phdApi',
  keepUnusedDataFor: 0,
  baseQuery: axiosFetchBase,
  tagTypes: [
    'PhdStudents',
    'PhdSupervisors',
    'PhdIndividualPlans',
    'PhdTheses',
    'PhdStudentEvaluations',
    'PhdDissertationExamApplications',
    'PhdDissertationExamExaminerProposals',
    'PhdDissertationDefenseApplications',
    'PhdDissertationDefenseExaminerProposals',
    'PhdSubjectsExamApplications',
    'PhdSchoolYears',
    'PhdComments',
    'PhdSystemStates',
    'PhdDocumentTemplates',
    'PhdDocumentVariables',
    'PhdIndividualPlanTasks',
    'PhdIndividualPlanSubjects',
    'PhdWorkingGroups',
    'PhdStudyFields',
    'PhdStudySubjects',
    'PhdDashboard',
    'PhdSchoolYearTransition',
    'PhdStudentDeadlines',
    'PhdStudyInterruptions',
  ],
  endpoints: (builder) => ({
    getPhdSystemStateCurrent: builder.query({
      query: () => ({
        url: urls.phdSystemStateCurrent,
      }),
      providesTags: ['PhdSystemStates'],
    }),
    getPhdDashboard: builder.query({
      query: (params) => ({
        url: urls.phdDashboard,
        params,
      }),
      providesTags: ['PhdDashboard'],
    }),
    getPhdSchoolYearTransitionPreview: builder.query({
      query: () => ({ url: urls.phdSchoolYearTransitionPreview }),
      providesTags: ['PhdSchoolYearTransition'],
    }),
    transitionPhdSchoolYear: builder.mutation({
      query: (data) => ({
        url: urls.phdSchoolYearTransition,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'PhdSchoolYearTransition',
        'PhdSchoolYears',
        'PhdDashboard',
      ],
    }),
    getCurrentPhdStudentProfile: builder.query({
      query: () => ({
        url: urls.phdStudentMe,
      }),
      providesTags: ['PhdStudents'],
    }),
    getPublicPhdTheses: builder.query({
      query: (params) => ({
        url: urls.phdTheses,
        params: {
          approved_only: true,
          ...params,
        },
      }),
      providesTags: ['PhdTheses'],
    }),
    getPublicPhdThesis: builder.query({
      query: (id) => ({
        url: urls.phdThesisDetail(id),
        params: {
          approved_only: true,
        },
      }),
      providesTags: (_result, _error, id) => [{ type: 'PhdTheses', id }],
    }),

    getPhdStudents: builder.query({
      query: (params) => ({
        url: urls.phdStudents,
        params,
      }),
      providesTags: ['PhdStudents'],
    }),
    getPhdStudent: builder.query({
      query: (id) => ({
        url: urls.phdStudentDetail(id),
      }),
      providesTags: (_result, _error, id) => [{ type: 'PhdStudents', id }],
    }),
    savePhdStudent: builder.mutation({
      query: ({ id, body }) => ({
        url: id ? urls.phdStudentDetail(id) : urls.phdStudents,
        method: id ? 'PUT' : 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdStudents'],
    }),
    deletePhdStudent: builder.mutation({
      query: (id) => ({
        url: urls.phdStudentDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdStudents'],
    }),
    getPhdStudentDeadlines: builder.query({
      query: (studentId) => ({
        url: urls.phdStudentDeadlines(studentId),
      }),
      providesTags: (_result, _error, studentId) => [
        { type: 'PhdStudentDeadlines', id: studentId },
      ],
    }),
    adjustPhdStudentDeadline: builder.mutation({
      query: ({ studentId, body }) => ({
        url: urls.phdStudentDeadlineAdjustments(studentId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { studentId }) => [
        { type: 'PhdStudentDeadlines', id: studentId },
        'PhdDashboard',
      ],
    }),
    getPhdStudentInterruptions: builder.query({
      query: (studentId) => ({
        url: urls.phdStudentInterruptions(studentId),
      }),
      providesTags: (_result, _error, studentId) => [
        { type: 'PhdStudyInterruptions', id: studentId },
      ],
    }),
    createPhdStudyInterruption: builder.mutation({
      query: ({ studentId, body }) => ({
        url: urls.phdStudentInterruptions(studentId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { studentId }) => [
        { type: 'PhdStudyInterruptions', id: studentId },
        { type: 'PhdStudentDeadlines', id: studentId },
        { type: 'PhdStudents', id: studentId },
        'PhdStudents',
        'PhdDashboard',
      ],
    }),
    resumePhdStudyInterruption: builder.mutation({
      query: ({ id, body }) => ({
        url: urls.phdStudyInterruptionResume(id),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { studentId }) => [
        { type: 'PhdStudyInterruptions', id: studentId },
        { type: 'PhdStudentDeadlines', id: studentId },
        { type: 'PhdStudents', id: studentId },
        'PhdStudents',
        'PhdDashboard',
      ],
    }),
    correctPhdStudyInterruption: builder.mutation({
      query: ({ id, body }) => ({
        url: urls.phdStudyInterruptionCorrection(id),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { studentId }) => [
        { type: 'PhdStudyInterruptions', id: studentId },
        { type: 'PhdStudentDeadlines', id: studentId },
        { type: 'PhdStudents', id: studentId },
        'PhdStudents',
        'PhdDashboard',
      ],
    }),

    getPhdSupervisors: builder.query({
      query: () => ({ url: urls.phdSupervisors }),
      providesTags: ['PhdSupervisors'],
    }),
    getPhdSupervisor: builder.query({
      query: (id) => ({ url: urls.phdSupervisorDetail(id) }),
      providesTags: (_result, _error, id) => [{ type: 'PhdSupervisors', id }],
    }),
    getPhdSupervisorCandidates: builder.query({
      query: (params) => ({ url: urls.phdSupervisorCandidates, params }),
    }),
    getPhdSupervisorReference: builder.query({
      query: () => ({ url: urls.phdSupervisorReference }),
      providesTags: ['PhdSupervisors'],
    }),
    savePhdSupervisor: builder.mutation({
      query: ({ id, body }) => ({
        url: id ? urls.phdSupervisorDetail(id) : urls.phdSupervisors,
        method: id ? 'PUT' : 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdSupervisors'],
    }),
    deletePhdSupervisor: builder.mutation({
      query: (id) => ({ url: urls.phdSupervisorDetail(id), method: 'DELETE' }),
      invalidatesTags: ['PhdSupervisors'],
    }),

    getPhdIndividualPlans: builder.query({
      query: (params) => ({
        url: urls.phdIndividualPlans,
        params,
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdIndividualPlans'],
    }),
    getPhdIndividualPlan: builder.query({
      query: (id) => ({
        url: urls.phdIndividualPlanDetail(id),
      }),
      transformResponse: presentPhdResult,
      providesTags: (_result, _error, id) => [
        { type: 'PhdIndividualPlans', id },
      ],
    }),
    getPhdIndividualPlanDocument: builder.query({
      query: (id) => ({
        url: urls.phdIndividualPlanDocument(id),
        responseType: 'blob',
      }),
    }),
    archivePhdIndividualPlanDocument: builder.mutation({
      query: (id) => ({
        url: urls.phdIndividualPlanDocumentArchive(id),
        method: 'POST',
        responseType: 'blob',
      }),
      invalidatesTags: ['PhdIndividualPlans'],
    }),
    savePhdIndividualPlan: builder.mutation({
      query: ({ id, body }) => ({
        url: id ? urls.phdIndividualPlanDetail(id) : urls.phdIndividualPlans,
        method: id ? 'PUT' : 'POST',
        data: withoutClientSchoolYear(body),
      }),
      invalidatesTags: ['PhdIndividualPlans'],
    }),
    deletePhdIndividualPlan: builder.mutation({
      query: (id) => ({
        url: urls.phdIndividualPlanDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdIndividualPlans'],
    }),
    getPhdIndividualPlanTasks: builder.query({
      query: (params) => ({
        url: urls.phdIndividualPlanTasks,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'PhdIndividualPlanTasks',
                id,
              })),
              { type: 'PhdIndividualPlanTasks', id: 'LIST' },
            ]
          : [{ type: 'PhdIndividualPlanTasks', id: 'LIST' }],
    }),
    savePhdIndividualPlanTask: builder.mutation({
      query: ({ id, body }) => ({
        url: id
          ? urls.phdIndividualPlanTaskDetail(id)
          : urls.phdIndividualPlanTasks,
        method: id ? 'PUT' : 'POST',
        data: body,
      }),
      invalidatesTags: [{ type: 'PhdIndividualPlanTasks', id: 'LIST' }],
    }),
    deletePhdIndividualPlanTask: builder.mutation({
      query: (id) => ({
        url: urls.phdIndividualPlanTaskDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'PhdIndividualPlanTasks', id: 'LIST' }],
    }),
    getPhdIndividualPlanSubjects: builder.query({
      query: (params) => ({
        url: urls.phdIndividualPlanSubjects,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'PhdIndividualPlanSubjects',
                id,
              })),
              { type: 'PhdIndividualPlanSubjects', id: 'LIST' },
            ]
          : [{ type: 'PhdIndividualPlanSubjects', id: 'LIST' }],
    }),
    getPhdTheses: builder.query({
      query: (params) => ({
        url: urls.phdTheses,
        params,
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdTheses'],
    }),
    getPhdThesis: builder.query({
      query: (id) => ({
        url: urls.phdThesisDetail(id),
      }),
      transformResponse: presentPhdResult,
      providesTags: (_result, _error, id) => [{ type: 'PhdTheses', id }],
    }),
    getPhdThesisDocument: builder.query({
      query: (id) => ({
        url: urls.phdThesisDocument(id),
        responseType: 'blob',
      }),
    }),
    getPhdThesesDocument: builder.query({
      query: (schoolYearId) => ({
        url: urls.phdThesesDocument,
        params: {
          phd_school_year_id: schoolYearId,
        },
        responseType: 'blob',
      }),
    }),
    getPhdThesisFile: builder.query({
      query: ({ id, type }) => ({
        url: urls.phdThesisFile(id, type),
        responseType: 'blob',
      }),
    }),
    bulkApprovePhdTheses: builder.mutation({
      query: (ids) => ({
        url: urls.phdThesesBulkApprove,
        method: 'POST',
        data: {
          ids,
        },
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    savePhdThesis: builder.mutation({
      query: ({ id, body }) => ({
        url: id ? urls.phdThesisDetail(id) : urls.phdTheses,
        method: id ? 'PUT' : 'POST',
        data: withoutClientSchoolYear(body),
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    deletePhdThesis: builder.mutation({
      query: (id) => ({
        url: urls.phdThesisDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    approvePhdThesis: builder.mutation({
      query: (id) => ({
        url: urls.phdThesisApprove(id),
        method: 'POST',
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    defendPhdThesis: builder.mutation({
      query: ({ id, body }) => ({
        url: urls.phdThesisDefend(id),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    manualDefendPhdThesis: builder.mutation({
      query: (body) => ({
        url: urls.phdThesesManualDefense,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    denyPhdThesis: builder.mutation({
      query: (id) => ({
        url: urls.phdThesisDeny(id),
        method: 'POST',
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    archivePhdThesis: builder.mutation({
      query: (id) => ({
        url: urls.phdThesisArchive(id),
        method: 'POST',
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    restorePhdThesis: builder.mutation({
      query: (id) => ({
        url: urls.phdThesisRestore(id),
        method: 'POST',
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    duplicatePhdThesis: builder.mutation({
      query: (id) => ({
        url: urls.phdThesisDuplicate(id),
        method: 'POST',
      }),
      invalidatesTags: ['PhdTheses'],
    }),
    replacePhdThesis: builder.mutation({
      query: ({ id, body }) => ({
        url: urls.phdThesisReplace(id),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdTheses', 'PhdStudents'],
    }),
    getPhdComments: builder.query({
      query: (params) => ({
        url: urls.phdComments,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'PhdComments', id })),
              { type: 'PhdComments', id: 'LIST' },
            ]
          : [{ type: 'PhdComments', id: 'LIST' }],
    }),
    getPhdComment: builder.query({
      query: (id) => ({
        url: urls.phdCommentDetail(id),
      }),
      providesTags: (_result, _error, id) => [{ type: 'PhdComments', id }],
    }),
    savePhdComment: builder.mutation({
      query: ({ id, body }) => ({
        url: id ? urls.phdCommentDetail(id) : urls.phdComments,
        method: id ? 'PUT' : 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { body }) => [
        { type: 'PhdComments', id: 'LIST' },
        ...(body?.phd_thesis_id
          ? [{ type: 'PhdTheses', id: body.phd_thesis_id }]
          : []),
      ],
    }),
    deletePhdComment: builder.mutation({
      query: (id) => ({
        url: urls.phdCommentDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'PhdComments', id: 'LIST' }],
    }),

    getPhdStudentEvaluations: builder.query({
      query: (params) => ({
        url: urls.phdStudentEvaluations,
        params,
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdStudentEvaluations'],
    }),
    getPhdStudentEvaluation: builder.query({
      query: (id) => ({
        url: urls.phdStudentEvaluationDetail(id),
      }),
      transformResponse: presentPhdResult,
      providesTags: (_result, _error, id) => [
        { type: 'PhdStudentEvaluations', id },
      ],
    }),
    getPhdStudentEvaluationDocument: builder.query({
      query: (id) => ({
        url: urls.phdStudentEvaluationDocument(id),
        responseType: 'blob',
      }),
    }),
    archivePhdStudentEvaluationDocument: builder.mutation({
      query: (id) => ({
        url: urls.phdStudentEvaluationDocumentArchive(id),
        method: 'POST',
        responseType: 'blob',
      }),
      invalidatesTags: ['PhdStudentEvaluations'],
    }),
    savePhdStudentEvaluation: builder.mutation({
      query: ({ id, body }) => ({
        url: id
          ? urls.phdStudentEvaluationDetail(id)
          : urls.phdStudentEvaluations,
        method: id ? 'PUT' : 'POST',
        data: withoutClientSchoolYear(body),
      }),
      invalidatesTags: ['PhdStudentEvaluations'],
    }),
    deletePhdStudentEvaluation: builder.mutation({
      query: (id) => ({
        url: urls.phdStudentEvaluationDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdStudentEvaluations'],
    }),
    getPhdStudentEvaluationGrades: builder.query({
      query: (params) => ({
        url: urls.phdStudentEvaluationGrades,
        params,
      }),
      providesTags: ['PhdStudentEvaluations'],
    }),
    getPhdStudentEvaluationGrade: builder.query({
      query: (id) => ({
        url: urls.phdStudentEvaluationGradeDetail(id),
      }),
      providesTags: (_result, _error, id) => [
        { type: 'PhdStudentEvaluations', id },
      ],
    }),
    savePhdStudentEvaluationGrade: builder.mutation({
      query: ({ id, body }) => ({
        url: id
          ? urls.phdStudentEvaluationGradeDetail(id)
          : urls.phdStudentEvaluationGrades,
        method: id ? 'PUT' : 'POST',
        data: withoutClientSchoolYear(body),
      }),
      invalidatesTags: ['PhdStudentEvaluations'],
    }),
    deletePhdStudentEvaluationGrade: builder.mutation({
      query: (id) => ({
        url: urls.phdStudentEvaluationGradeDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdStudentEvaluations'],
    }),

    getPhdDissertationExamApplications: builder.query({
      query: (params) => ({
        url: urls.phdDissertationExamApplications,
        params,
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdDissertationExamApplications'],
    }),
    getSubmittedPhdDissertationExamApplications: builder.query({
      query: () => ({
        url: urls.phdDissertationExamApplications,
        params: { submitted: true },
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdDissertationExamApplications'],
    }),
    getPhdDissertationExamApplication: builder.query({
      query: (id) => ({
        url: urls.phdDissertationExamApplicationDetail(id),
      }),
      transformResponse: presentPhdResult,
      providesTags: (_result, _error, id) => [
        { type: 'PhdDissertationExamApplications', id },
      ],
    }),
    getPhdDissertationExamApplicationDocument: builder.query({
      query: (id) => ({
        url: urls.phdDissertationExamApplicationDocument(id),
        responseType: 'blob',
      }),
    }),
    savePhdDissertationExamApplication: builder.mutation({
      query: ({ id, body }) => ({
        url: id
          ? urls.phdDissertationExamApplicationDetail(id)
          : urls.phdDissertationExamApplications,
        method: id ? 'PUT' : 'POST',
        data: withoutClientSchoolYear(body),
      }),
      invalidatesTags: ['PhdDissertationExamApplications'],
    }),
    deletePhdDissertationExamApplication: builder.mutation({
      query: (id) => ({
        url: urls.phdDissertationExamApplicationDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdDissertationExamApplications'],
    }),
    submitPhdDissertationExamApplication: builder.mutation({
      query: (id) => ({
        url: urls.phdDissertationExamApplicationSubmit(id),
        method: 'POST',
      }),
      invalidatesTags: ['PhdDissertationExamApplications'],
    }),

    getPhdDissertationExamExaminerProposals: builder.query({
      query: (params) => ({
        url: urls.phdDissertationExamExaminerProposals,
        params,
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdDissertationExamExaminerProposals'],
    }),
    getPhdDissertationExamExaminerProposal: builder.query({
      query: (id) => ({
        url: urls.phdDissertationExamExaminerProposalDetail(id),
      }),
      transformResponse: presentPhdResult,
      providesTags: (_result, _error, id) => [
        { type: 'PhdDissertationExamExaminerProposals', id },
      ],
    }),
    getPhdDissertationExamExaminerProposalDocument: builder.query({
      query: (id) => ({
        url: urls.phdDissertationExamExaminerProposalDocument(id),
        responseType: 'blob',
      }),
    }),
    archivePhdDissertationExamExaminerProposalDocument: builder.mutation({
      query: (id) => ({
        url: urls.phdDissertationExamExaminerProposalDocumentArchive(id),
        method: 'POST',
        responseType: 'blob',
      }),
      invalidatesTags: ['PhdDissertationExamExaminerProposals'],
    }),
    savePhdDissertationExamExaminerProposal: builder.mutation({
      query: ({ id, body }) => ({
        url: id
          ? urls.phdDissertationExamExaminerProposalDetail(id)
          : urls.phdDissertationExamExaminerProposals,
        method: id ? 'PUT' : 'POST',
        data: withoutClientSchoolYear(body),
      }),
      invalidatesTags: ['PhdDissertationExamExaminerProposals'],
    }),
    deletePhdDissertationExamExaminerProposal: builder.mutation({
      query: (id) => ({
        url: urls.phdDissertationExamExaminerProposalDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdDissertationExamExaminerProposals'],
    }),

    getPhdDissertationDefenseApplications: builder.query({
      query: (params) => ({
        url: urls.phdDissertationDefenseApplications,
        params,
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdDissertationDefenseApplications'],
    }),
    getSubmittedPhdDissertationDefenseApplications: builder.query({
      query: () => ({
        url: urls.phdDissertationDefenseApplications,
        params: { submitted: true },
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdDissertationDefenseApplications'],
    }),
    getPhdDissertationDefenseApplication: builder.query({
      query: (id) => ({
        url: urls.phdDissertationDefenseApplicationDetail(id),
      }),
      transformResponse: presentPhdResult,
      providesTags: (_result, _error, id) => [
        { type: 'PhdDissertationDefenseApplications', id },
      ],
    }),
    getPhdDissertationDefenseApplicationDocument: builder.query({
      query: (id) => ({
        url: urls.phdDissertationDefenseApplicationDocument(id),
        responseType: 'blob',
      }),
    }),
    savePhdDissertationDefenseApplication: builder.mutation({
      query: ({ id, body }) => ({
        url: id
          ? urls.phdDissertationDefenseApplicationDetail(id)
          : urls.phdDissertationDefenseApplications,
        method: id ? 'PUT' : 'POST',
        data: withoutClientSchoolYear(body),
      }),
      invalidatesTags: ['PhdDissertationDefenseApplications'],
    }),
    deletePhdDissertationDefenseApplication: builder.mutation({
      query: (id) => ({
        url: urls.phdDissertationDefenseApplicationDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdDissertationDefenseApplications'],
    }),
    submitPhdDissertationDefenseApplication: builder.mutation({
      query: (id) => ({
        url: urls.phdDissertationDefenseApplicationSubmit(id),
        method: 'POST',
      }),
      invalidatesTags: ['PhdDissertationDefenseApplications'],
    }),

    getPhdDissertationDefenseExaminerProposals: builder.query({
      query: (params) => ({
        url: urls.phdDissertationDefenseExaminerProposals,
        params,
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdDissertationDefenseExaminerProposals'],
    }),
    getPhdDissertationDefenseExaminerProposal: builder.query({
      query: (id) => ({
        url: urls.phdDissertationDefenseExaminerProposalDetail(id),
      }),
      transformResponse: presentPhdResult,
      providesTags: (_result, _error, id) => [
        { type: 'PhdDissertationDefenseExaminerProposals', id },
      ],
    }),
    getPhdDissertationDefenseExaminerProposalDocument: builder.query({
      query: (id) => ({
        url: urls.phdDissertationDefenseExaminerProposalDocument(id),
        responseType: 'blob',
      }),
    }),
    archivePhdDissertationDefenseExaminerProposalDocument: builder.mutation({
      query: (id) => ({
        url: urls.phdDissertationDefenseExaminerProposalDocumentArchive(id),
        method: 'POST',
        responseType: 'blob',
      }),
      invalidatesTags: ['PhdDissertationDefenseExaminerProposals'],
    }),
    savePhdDissertationDefenseExaminerProposal: builder.mutation({
      query: ({ id, body }) => ({
        url: id
          ? urls.phdDissertationDefenseExaminerProposalDetail(id)
          : urls.phdDissertationDefenseExaminerProposals,
        method: id ? 'PUT' : 'POST',
        data: withoutClientSchoolYear(body),
      }),
      invalidatesTags: ['PhdDissertationDefenseExaminerProposals'],
    }),
    deletePhdDissertationDefenseExaminerProposal: builder.mutation({
      query: (id) => ({
        url: urls.phdDissertationDefenseExaminerProposalDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdDissertationDefenseExaminerProposals'],
    }),

    getPhdSubjectsExamApplications: builder.query({
      query: (params) => ({
        url: urls.phdSubjectsExamApplications,
        params,
      }),
      transformResponse: presentPhdResult,
      providesTags: ['PhdSubjectsExamApplications'],
    }),
    getPhdSubjectsExamApplication: builder.query({
      query: (id) => ({
        url: urls.phdSubjectsExamApplicationDetail(id),
      }),
      transformResponse: presentPhdResult,
      providesTags: (_result, _error, id) => [
        { type: 'PhdSubjectsExamApplications', id },
      ],
    }),
    getPhdSubjectsExamApplicationDocument: builder.query({
      query: (id) => ({
        url: urls.phdSubjectsExamApplicationDocument(id),
        responseType: 'blob',
      }),
    }),
    savePhdSubjectsExamApplication: builder.mutation({
      query: ({ id, body }) => ({
        url: id
          ? urls.phdSubjectsExamApplicationDetail(id)
          : urls.phdSubjectsExamApplications,
        method: id ? 'PUT' : 'POST',
        data: withoutClientSchoolYear(body),
      }),
      invalidatesTags: ['PhdSubjectsExamApplications'],
    }),
    deletePhdSubjectsExamApplication: builder.mutation({
      query: (id) => ({
        url: urls.phdSubjectsExamApplicationDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdSubjectsExamApplications'],
    }),
    submitPhdSubjectsExamApplication: builder.mutation({
      query: (id) => ({
        url: urls.phdSubjectsExamApplicationSubmit(id),
        method: 'POST',
      }),
      invalidatesTags: ['PhdSubjectsExamApplications'],
    }),

    getPhdSchoolYears: builder.query({
      query: () => ({
        url: urls.phdSchoolYears,
      }),
      providesTags: ['PhdSchoolYears'],
    }),
    getPhdSystemStates: builder.query({
      query: (params) => ({
        url: urls.phdSystemStates,
        params,
      }),
      providesTags: ['PhdSystemStates'],
    }),
    getPhdDocumentTemplates: builder.query({
      query: () => ({
        url: urls.phdDocumentTemplates,
      }),
      providesTags: ['PhdDocumentTemplates'],
    }),
    getPhdDocumentTemplate: builder.query({
      query: (key) => ({
        url: urls.phdDocumentTemplateDetail(key),
        responseType: 'blob',
      }),
    }),
    savePhdDocumentTemplate: builder.mutation({
      query: ({ key, file }) => {
        const data = new FormData();
        data.append('template', file);

        return {
          url: urls.phdDocumentTemplateDetail(key),
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['PhdDocumentTemplates'],
    }),
    previewPhdDocumentTemplate: builder.query({
      query: (key) => ({
        url: urls.phdDocumentTemplatePreview(key),
        responseType: 'blob',
      }),
    }),
    getPhdDocumentVariables: builder.query({
      query: () => ({
        url: urls.phdDocumentVariables,
      }),
      providesTags: ['PhdDocumentVariables'],
    }),
    savePhdDocumentVariable: builder.mutation({
      query: ({ id, body }) => ({
        url: id
          ? urls.phdDocumentVariableDetail(id)
          : urls.phdDocumentVariables,
        method: id ? 'PUT' : 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdDocumentVariables', 'PhdDocumentTemplates'],
    }),
    deletePhdDocumentVariable: builder.mutation({
      query: (id) => ({
        url: urls.phdDocumentVariableDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdDocumentVariables', 'PhdDocumentTemplates'],
    }),
    savePhdSystemState: builder.mutation({
      query: ({ body }) => ({
        url: urls.phdSystemStates,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdSystemStates'],
    }),
    getPhdWorkingGroups: builder.query({
      query: () => ({
        url: urls.phdWorkingGroups,
      }),
      providesTags: ['PhdWorkingGroups'],
    }),
    getPhdWorkingGroupCandidates: builder.query({
      query: () => ({
        url: urls.phdWorkingGroups,
        params: {
          candidates: true,
        },
      }),
    }),
    savePhdWorkingGroup: builder.mutation({
      query: ({ id, body }) => ({
        url: id ? urls.phdWorkingGroupDetail(id) : urls.phdWorkingGroups,
        method: id ? 'PUT' : 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdWorkingGroups'],
    }),
    getPhdStudyFields: builder.query({
      query: () => ({
        url: urls.phdStudyFields,
      }),
      providesTags: ['PhdStudyFields'],
    }),
    savePhdStudyField: builder.mutation({
      query: ({ id, body }) => ({
        url: id ? urls.phdStudyFieldDetail(id) : urls.phdStudyFields,
        method: id ? 'PUT' : 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdStudyFields', 'PhdStudySubjects'],
    }),
    deletePhdStudyField: builder.mutation({
      query: (id) => ({
        url: urls.phdStudyFieldDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdStudyFields', 'PhdStudySubjects'],
    }),
    getPhdStudySubjects: builder.query({
      query: () => ({
        url: urls.phdStudySubjects,
      }),
      providesTags: ['PhdStudySubjects'],
    }),
    savePhdStudySubject: builder.mutation({
      query: ({ id, body }) => ({
        url: id ? urls.phdStudySubjectDetail(id) : urls.phdStudySubjects,
        method: id ? 'PUT' : 'POST',
        data: body,
      }),
      invalidatesTags: ['PhdStudySubjects', 'PhdStudyFields'],
    }),
    deletePhdStudySubject: builder.mutation({
      query: (id) => ({
        url: urls.phdStudySubjectDetail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PhdStudySubjects', 'PhdStudyFields'],
    }),
  }),
});

export const {
  useGetPhdStudentsQuery,
  useGetPhdDashboardQuery,
  useGetPhdSchoolYearTransitionPreviewQuery,
  useTransitionPhdSchoolYearMutation,
  useGetPhdStudentQuery,
  useSavePhdStudentMutation,
  useDeletePhdStudentMutation,
  useGetPhdStudentDeadlinesQuery,
  useAdjustPhdStudentDeadlineMutation,
  useGetPhdStudentInterruptionsQuery,
  useCreatePhdStudyInterruptionMutation,
  useResumePhdStudyInterruptionMutation,
  useCorrectPhdStudyInterruptionMutation,
  useGetPhdSupervisorsQuery,
  useGetPhdSupervisorQuery,
  useGetPhdSupervisorCandidatesQuery,
  useGetPhdSupervisorReferenceQuery,
  useSavePhdSupervisorMutation,
  useDeletePhdSupervisorMutation,
  useGetPhdIndividualPlansQuery,
  useGetPhdIndividualPlanQuery,
  useLazyGetPhdIndividualPlanDocumentQuery,
  useArchivePhdIndividualPlanDocumentMutation,
  useSavePhdIndividualPlanMutation,
  useDeletePhdIndividualPlanMutation,
  useGetPhdIndividualPlanTasksQuery,
  useSavePhdIndividualPlanTaskMutation,
  useDeletePhdIndividualPlanTaskMutation,
  useGetPhdIndividualPlanSubjectsQuery,
  useGetPhdThesesQuery,
  useGetPhdThesisQuery,
  useLazyGetPhdThesisQuery,
  useLazyGetPhdThesisDocumentQuery,
  useLazyGetPhdThesesDocumentQuery,
  useLazyGetPhdThesisFileQuery,
  useBulkApprovePhdThesesMutation,
  useSavePhdThesisMutation,
  useDeletePhdThesisMutation,
  useApprovePhdThesisMutation,
  useDefendPhdThesisMutation,
  useManualDefendPhdThesisMutation,
  useDenyPhdThesisMutation,
  useArchivePhdThesisMutation,
  useRestorePhdThesisMutation,
  useDuplicatePhdThesisMutation,
  useReplacePhdThesisMutation,
  useGetPhdStudentEvaluationsQuery,
  useGetPhdStudentEvaluationQuery,
  useLazyGetPhdStudentEvaluationDocumentQuery,
  useArchivePhdStudentEvaluationDocumentMutation,
  useSavePhdStudentEvaluationMutation,
  useDeletePhdStudentEvaluationMutation,
  useGetPhdStudentEvaluationGradesQuery,
  useGetPhdStudentEvaluationGradeQuery,
  useSavePhdStudentEvaluationGradeMutation,
  useDeletePhdStudentEvaluationGradeMutation,
  useGetPhdDissertationExamApplicationsQuery,
  useGetSubmittedPhdDissertationExamApplicationsQuery,
  useGetPhdDissertationExamApplicationQuery,
  useLazyGetPhdDissertationExamApplicationDocumentQuery,
  useSavePhdDissertationExamApplicationMutation,
  useDeletePhdDissertationExamApplicationMutation,
  useSubmitPhdDissertationExamApplicationMutation,
  useGetPhdDissertationExamExaminerProposalsQuery,
  useGetPhdDissertationExamExaminerProposalQuery,
  useLazyGetPhdDissertationExamExaminerProposalDocumentQuery,
  useArchivePhdDissertationExamExaminerProposalDocumentMutation,
  useSavePhdDissertationExamExaminerProposalMutation,
  useDeletePhdDissertationExamExaminerProposalMutation,
  useGetPhdDissertationDefenseApplicationsQuery,
  useGetSubmittedPhdDissertationDefenseApplicationsQuery,
  useGetPhdDissertationDefenseApplicationQuery,
  useLazyGetPhdDissertationDefenseApplicationDocumentQuery,
  useSavePhdDissertationDefenseApplicationMutation,
  useDeletePhdDissertationDefenseApplicationMutation,
  useSubmitPhdDissertationDefenseApplicationMutation,
  useGetPhdDissertationDefenseExaminerProposalsQuery,
  useGetPhdDissertationDefenseExaminerProposalQuery,
  useLazyGetPhdDissertationDefenseExaminerProposalDocumentQuery,
  useArchivePhdDissertationDefenseExaminerProposalDocumentMutation,
  useSavePhdDissertationDefenseExaminerProposalMutation,
  useDeletePhdDissertationDefenseExaminerProposalMutation,
  useGetPhdSubjectsExamApplicationsQuery,
  useGetPhdSubjectsExamApplicationQuery,
  useLazyGetPhdSubjectsExamApplicationDocumentQuery,
  useSavePhdSubjectsExamApplicationMutation,
  useDeletePhdSubjectsExamApplicationMutation,
  useSubmitPhdSubjectsExamApplicationMutation,
  useGetPhdSchoolYearsQuery,
  useGetPublicPhdThesesQuery,
  useGetPublicPhdThesisQuery,
  useGetPhdSystemStateCurrentQuery,
  useGetPhdSystemStatesQuery,
  useGetPhdDocumentTemplatesQuery,
  useLazyGetPhdDocumentTemplateQuery,
  useSavePhdDocumentTemplateMutation,
  useLazyPreviewPhdDocumentTemplateQuery,
  useGetPhdDocumentVariablesQuery,
  useSavePhdDocumentVariableMutation,
  useDeletePhdDocumentVariableMutation,
  useSavePhdSystemStateMutation,
  useGetCurrentPhdStudentProfileQuery,
  useGetPhdCommentsQuery,
  useGetPhdCommentQuery,
  useSavePhdCommentMutation,
  useDeletePhdCommentMutation,
  useGetPhdWorkingGroupsQuery,
  useGetPhdWorkingGroupCandidatesQuery,
  useSavePhdWorkingGroupMutation,
  useGetPhdStudyFieldsQuery,
  useSavePhdStudyFieldMutation,
  useDeletePhdStudyFieldMutation,
  useGetPhdStudySubjectsQuery,
  useSavePhdStudySubjectMutation,
  useDeletePhdStudySubjectMutation,
} = phdApi;
